import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  message,
  Typography,
  Row,
  Col,
  Avatar,
  Space,
  Divider,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  UploadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(
  /\/api$/,
  '',
);

const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

const { Title, Text } = Typography;
const { TextArea } = Input;

const Profile = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery(
    'provider-profile',
    async () => {
      const response = await api.get('/provider/profile');
      return response.data.data || response.data;
    },
    {
      onSuccess: (data) => {
        if (data) {
          // Convert service_areas to comma-separated string for display
          let serviceAreasValue = '';
          if (Array.isArray(data.service_areas)) {
            serviceAreasValue = data.service_areas.join(', ');
          } else if (typeof data.service_areas === 'string') {
            // Handle JSON string like '["Kathmandu","Lalitpur"]'
            try {
              const parsed = JSON.parse(data.service_areas);
              serviceAreasValue = Array.isArray(parsed) ? parsed.join(', ') : data.service_areas;
            } catch {
              serviceAreasValue = data.service_areas;
            }
          }

          const formattedData = {
            ...data,
            service_areas: serviceAreasValue,
          };
          form.setFieldsValue(formattedData);
          setPreviewImage(null); // Reset preview when profile loads
          setProfileImageFile(null); // Reset file state when profile loads
        }
      },
    },
  );

  const updateProfileMutation = useMutation(
    async (profileData) => {
      const formData = new FormData();

      Object.keys(profileData).forEach((key) => {
        if (key === 'profile_image') {
          return;
        }
        if (profileData[key] !== null && profileData[key] !== undefined) {
          const value = profileData[key];
          if (typeof value === 'boolean') {
            formData.append(key, value ? 'true' : 'false');
          } else {
            formData.append(key, value);
          }
        }
      });

      if (profileImageFile) {
        const file = profileImageFile.originFileObj || profileImageFile;
        if (file instanceof File) {
          formData.append('profile_image', file);
        }
      }

      const response = await api.post('/provider/profile-update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data;
    },
    {
      onSuccess: (response) => {
        message.success('Profile updated successfully');

        // Get the updated profile data from the response
        const updatedProfile = response.data?.data || response.data;

        // Update the cache directly with new data
        queryClient.setQueryData('provider-profile', () => {
          return updatedProfile;
        });

        // Update form with the new data immediately
        setPreviewImage(null);
        setProfileImageFile(null);

        // Format service_areas for display
        let serviceAreasValue = '';
        if (updatedProfile && Array.isArray(updatedProfile.service_areas)) {
          serviceAreasValue = updatedProfile.service_areas.join(', ');
        } else if (updatedProfile && typeof updatedProfile.service_areas === 'string') {
          try {
            const parsed = JSON.parse(updatedProfile.service_areas);
            serviceAreasValue = Array.isArray(parsed)
              ? parsed.join(', ')
              : updatedProfile.service_areas;
          } catch {
            serviceAreasValue = updatedProfile.service_areas;
          }
        }

        const formattedData = {
          ...updatedProfile,
          service_areas: serviceAreasValue,
        };

        form.setFieldsValue({
          ...formattedData,
          profile_image: null,
        });
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update profile');
      },
    },
  );

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Convert service_areas comma-separated string back to array
      const submitData = { ...values };
      if (submitData.service_areas && typeof submitData.service_areas === 'string') {
        submitData.service_areas = submitData.service_areas
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s);
      }

      // Remove profile_image - we use profileImageFile from state instead
      delete submitData.profile_image;

      await updateProfileMutation.mutateAsync(submitData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    name: 'profile_image',
    showUploadList: false,
    beforeUpload: () => false,
    accept: 'image/*',
    onChange: ({ fileList }) => {
      const latestFile = fileList[fileList.length - 1];
      if (latestFile) {
        setProfileImageFile(latestFile);
        const reader = new FileReader();
        reader.onload = () => {
          setPreviewImage(reader.result);
        };
        reader.readAsDataURL(latestFile.originFileObj || latestFile);
      }
    },
  };

  if (isLoading) {
    return <div>Loading profile...</div>;
  }

  return (
    <div>
      <Title level={2} className="page-title">
        Profile Settings
      </Title>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <Card className="profile-section">
            <div style={{ textAlign: 'center' }}>
              <Avatar
                size={120}
                src={previewImage || getFullUrl(profile?.profile_image)}
                icon={<UserOutlined />}
                style={{ marginBottom: 16 }}
              />
              <Title level={4}>{profile?.full_name || profile?.name}</Title>
              <Text type="secondary">{profile?.email}</Text>
              <Divider />
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Member Since: </Text>
                  <Text>{new Date(profile?.created_at).toLocaleDateString()}</Text>
                </div>
                <div>
                  <Text strong>Account Status: </Text>
                  <Text>
                    {profile?.is_active ? (
                      <span style={{ color: '#10b981' }}>✓ Active</span>
                    ) : (
                      <span style={{ color: '#ff4d4f' }}>✗ Inactive</span>
                    )}
                  </Text>
                </div>
              </Space>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card className="profile-section">
            <Title level={4}>Edit Profile</Title>
            <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={profile}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="full_name"
                    label="Full Name"
                    rules={[{ required: true, message: 'Please enter your full name' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="Enter your full name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone_number"
                    label="Phone Number"
                    rules={[
                      { required: true, message: 'Please enter your phone number' },
                      { pattern: /^[+]?[\d\s-()]+$/, message: 'Please enter a valid phone number' },
                    ]}
                  >
                    <Input
                      type="tel"
                      onKeyPress={(e) =>
                        e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()
                      }
                      prefix={<PhoneOutlined />}
                      placeholder="Enter your phone number"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Enter your email"
                  disabled // Email should not be changeable
                />
              </Form.Item>

              <Form.Item name="business_name" label="Business Name">
                <Input placeholder="Enter your business name" />
              </Form.Item>

              <Form.Item name="business_description" label="Business Description">
                <TextArea rows={3} placeholder="Describe your business" />
              </Form.Item>

              <Form.Item name="service_areas" label="Service Areas">
                <TextArea rows={2} placeholder="Enter the areas you serve (comma separated)" />
              </Form.Item>

              <Form.Item name="profile_image" label="Profile Image">
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading || updateProfileMutation.isLoading}
                    icon={<SaveOutlined />}
                  >
                    Save Changes
                  </Button>
                  <Button onClick={() => form.resetFields()}>Reset</Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
